import * as React from 'react'

// Defensive import - Datadog might be blocked by WAF or fail to load
let datadogLogs: typeof import('@datadog/browser-logs').datadogLogs | null =
    null
try {
    const datadog = require('@datadog/browser-logs')
    datadogLogs = datadog.datadogLogs
} catch (error) {
    console.warn('Datadog logs unavailable in ErrorBoundary:', error)
}

// Types
interface ErrorBoundaryProps {
    children: React.ReactNode
    branding?: {
        contactLink: string
    }
    fallback?: React.ReactNode
    onError?: (error: Error, errorInfo: React.ErrorInfo) => void
    onReset?: () => void
    resetKeys?: any[]
    level?: 'app' | 'page' | 'component'
    componentName?: string
}

interface ErrorBoundaryState {
    hasError: boolean
    error: Error | null
    errorInfo: React.ErrorInfo | null
}

// Logging utility
const logError = (
    error: Error,
    errorInfo: React.ErrorInfo,
    level: string,
    componentName?: string
) => {
    if (process.env.NODE_ENV === 'production' && datadogLogs) {
        try {
            datadogLogs.logger.error(
                'React Error Boundary caught error',
                {
                    errorMessage: error.message,
                    componentStack: errorInfo.componentStack,
                    level,
                    componentName,
                },
                error
            )
        } catch (err) {
            // Fallback to console if Datadog fails
            console.error(
                `[${level}] Error caught by boundary:`,
                error,
                errorInfo
            )
            console.warn('Failed to log error to Datadog:', err)
        }
    } else {
        // In development, log to console for debugging
        console.error(`[${level}] Error caught by boundary:`, error, errorInfo)
    }
}

/**
 * Base Error Boundary class with configurable fallback UI
 *
 * NOTE: Error Boundaries MUST be class components - React does not support
 * error boundary functionality with hooks/function components. This is a
 * React limitation, not a design choice.
 * See: https://react.dev/reference/react/Component#catching-rendering-errors-with-an-error-boundary
 */
class ErrorBoundary extends React.Component<
    ErrorBoundaryProps,
    ErrorBoundaryState
> {
    constructor(props: ErrorBoundaryProps) {
        super(props)
        this.state = {
            hasError: false,
            error: null,
            errorInfo: null,
        }
    }

    static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
        return {
            hasError: true,
            error,
        }
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
        this.setState({ errorInfo })
        logError(
            error,
            errorInfo,
            this.props.level || 'component',
            this.props.componentName
        )
        this.props.onError?.(error, errorInfo)
    }

    componentDidUpdate(prevProps: ErrorBoundaryProps) {
        // Reset error state when resetKeys change
        if (
            this.state.hasError &&
            this.props.resetKeys &&
            prevProps.resetKeys &&
            !this.areResetKeysEqual(prevProps.resetKeys, this.props.resetKeys)
        ) {
            this.reset()
        }
    }

    areResetKeysEqual(prev: any[], next: any[]): boolean {
        if (prev.length !== next.length) return false
        return prev.every((key, index) => key === next[index])
    }

    reset = () => {
        this.setState({ hasError: false, error: null, errorInfo: null })
        this.props.onReset?.()
    }

    render() {
        if (this.state.hasError) {
            if (this.props.fallback) {
                return this.props.fallback
            }

            return (
                <ErrorFallbackUI
                    error={this.state.error}
                    level={this.props.level || 'component'}
                    contactLink={this.props.branding?.contactLink}
                    onRetry={this.reset}
                />
            )
        }

        return this.props.children
    }
}

export default ErrorBoundary

/**
 * Reusable error fallback UI component
 */
interface ErrorFallbackUIProps {
    error: Error | null
    level: 'app' | 'page' | 'component'
    contactLink?: string
    onRetry?: () => void
}

const ErrorFallbackUI: React.FC<ErrorFallbackUIProps> = ({
    error,
    level,
    contactLink,
    onRetry,
}) => {
    const isFullPage = level === 'app' || level === 'page'

    const containerStyle: React.CSSProperties = isFullPage
        ? {
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: level === 'app' ? '100vh' : '100%',
              minHeight: level === 'page' ? '300px' : undefined,
              padding: '20px',
              textAlign: 'center',
              backgroundColor: '#f8f9fa',
          }
        : {
              padding: '16px',
              backgroundColor: '#fff5f5',
              borderRadius: '8px',
              border: '1px solid #fed7d7',
              margin: '8px 0',
          }

    const cardStyle: React.CSSProperties = isFullPage
        ? {
              maxWidth: '500px',
              backgroundColor: 'white',
              padding: '30px',
              borderRadius: '8px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              fontFamily: 'Roboto, sans-serif',
          }
        : {}

    const title =
        level === 'app'
            ? 'Something went wrong'
            : level === 'page'
            ? 'This page encountered an error'
            : 'Something went wrong in this section'

    const message =
        level === 'app'
            ? 'We encountered an error while loading the application. This may be due to browser compatibility issues.'
            : level === 'page'
            ? 'This page failed to load. You can try again or navigate to another page.'
            : 'This section failed to load. Please try again.'

    return (
        <div style={containerStyle}>
            <div style={cardStyle}>
                <h2
                    style={{
                        color: '#dc3545',
                        marginBottom: '16px',
                        fontSize: isFullPage ? '24px' : '16px',
                    }}
                >
                    {title}
                </h2>
                <p
                    style={{
                        color: '#6c757d',
                        marginBottom: '20px',
                        fontSize: isFullPage ? '14px' : '13px',
                    }}
                >
                    {message}
                    {contactLink && level === 'app' && (
                        <>
                            {' '}
                            If this persists, please contact us at {contactLink}
                        </>
                    )}
                </p>

                {onRetry && (
                    <button
                        onClick={onRetry}
                        style={{
                            backgroundColor: '#28a745',
                            color: 'white',
                            border: 'none',
                            padding: '10px 20px',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            marginBottom: '16px',
                        }}
                    >
                        Try Again
                    </button>
                )}

                <details style={{ textAlign: 'left', marginTop: '16px' }}>
                    <summary
                        style={{
                            cursor: 'pointer',
                            color: '#007bff',
                            marginBottom: '10px',
                            fontSize: '13px',
                        }}
                    >
                        Error details
                    </summary>
                    <code
                        style={{
                            display: 'block',
                            padding: '10px',
                            backgroundColor: '#f8f9fa',
                            borderRadius: '4px',
                            fontSize: '12px',
                            overflowX: 'auto',
                            color: '#dc3545',
                        }}
                    >
                        {error?.toString() || 'Unknown error'}
                    </code>
                </details>
            </div>
        </div>
    )
}

export { ErrorFallbackUI }

/**
 * Higher-Order Component for wrapping components with error boundaries
 */
export function withErrorBoundary<P extends object>(
    WrappedComponent: React.ComponentType<P>,
    options: {
        level?: 'page' | 'component'
        componentName?: string
        fallback?: React.ReactNode
        onError?: (error: Error, errorInfo: React.ErrorInfo) => void
    } = {}
): React.FC<P> {
    const { level = 'component', componentName, fallback, onError } = options

    const displayName =
        componentName ||
        WrappedComponent.displayName ||
        WrappedComponent.name ||
        'Component'

    const WithErrorBoundary: React.FC<P> = (props) => (
        <ErrorBoundary
            level={level}
            componentName={displayName}
            fallback={fallback}
            onError={onError}
        >
            <WrappedComponent {...props} />
        </ErrorBoundary>
    )

    WithErrorBoundary.displayName = `withErrorBoundary(${displayName})`

    return WithErrorBoundary
}

/**
 * Styled Page Error Fallback - matches app design
 */
interface PageErrorFallbackProps {
    error: Error | null
    onRetry: () => void
    pageName: string
}

const PageErrorFallback: React.FC<PageErrorFallbackProps> = ({
    error,
    onRetry,
    pageName,
}) => {
    const [showDetails, setShowDetails] = React.useState(false)

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '40px 20px',
                minHeight: '350px',
                fontFamily: 'Roboto, sans-serif',
                backgroundColor: '#364165',
                borderRadius: '12px',
                margin: '16px',
            }}
        >
            {/* Title */}
            <h2
                style={{
                    fontSize: '20px',
                    fontWeight: 600,
                    color: '#FFFFFF',
                    marginBottom: '12px',
                    textAlign: 'center',
                }}
            >
                Something went wrong
            </h2>

            {/* Description */}
            <p
                style={{
                    fontSize: '14px',
                    color: '#ffffff80',
                    marginBottom: '28px',
                    textAlign: 'center',
                    maxWidth: '280px',
                    lineHeight: '1.6',
                }}
            >
                We couldn't load this page. Try again or head back home.
            </p>

            {/* Action Buttons */}
            <div
                style={{
                    display: 'flex',
                    gap: '12px',
                    marginBottom: '24px',
                }}
            >
                <button
                    onClick={onRetry}
                    style={{
                        backgroundColor: '#1FB723',
                        color: 'white',
                        border: 'none',
                        padding: '12px 28px',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: 500,
                        transition: 'background-color 0.2s',
                    }}
                    onMouseOver={(e) =>
                        (e.currentTarget.style.backgroundColor = '#48d74b')
                    }
                    onMouseOut={(e) =>
                        (e.currentTarget.style.backgroundColor = '#1FB723')
                    }
                >
                    Try Again
                </button>
                <button
                    onClick={() => window.location.reload()}
                    style={{
                        backgroundColor: 'transparent',
                        color: '#FFFFFF',
                        border: '1px solid #ffffff40',
                        padding: '12px 28px',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: 500,
                        transition: 'all 0.2s',
                    }}
                    onMouseOver={(e) => {
                        e.currentTarget.style.backgroundColor = '#ffffff10'
                        e.currentTarget.style.borderColor = '#ffffff60'
                    }}
                    onMouseOut={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent'
                        e.currentTarget.style.borderColor = '#ffffff40'
                    }}
                >
                    Reload
                </button>
            </div>

            {/* Error Details Toggle */}
            <button
                onClick={() => setShowDetails(!showDetails)}
                style={{
                    background: 'none',
                    border: 'none',
                    color: '#ffffff60',
                    fontSize: '12px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    transition: 'color 0.2s',
                }}
                onMouseOver={(e) => (e.currentTarget.style.color = '#ffffff90')}
                onMouseOut={(e) => (e.currentTarget.style.color = '#ffffff60')}
            >
                {showDetails ? '▾' : '▸'} View error details
            </button>

            {/* Error Details */}
            {showDetails && (
                <div
                    style={{
                        marginTop: '16px',
                        padding: '14px',
                        backgroundColor: '#2a3454',
                        borderRadius: '8px',
                        maxWidth: '100%',
                        width: 'calc(100% - 32px)',
                        boxSizing: 'border-box',
                    }}
                >
                    <div
                        style={{
                            fontSize: '11px',
                            color: '#ffffff50',
                            marginBottom: '6px',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                        }}
                    >
                        {pageName} Error
                    </div>
                    <code
                        style={{
                            display: 'block',
                            fontSize: '12px',
                            color: '#f65656',
                            wordBreak: 'break-word',
                            whiteSpace: 'pre-wrap',
                            lineHeight: '1.5',
                        }}
                    >
                        {error?.message || 'Unknown error'}
                    </code>
                </div>
            )}
        </div>
    )
}

/**
 * Page-level error boundary component with custom styled fallback
 */
interface PageErrorBoundaryProps {
    children: React.ReactNode
    pageName: string
    onError?: (error: Error, errorInfo: React.ErrorInfo) => void
}

export const PageErrorBoundary: React.FC<PageErrorBoundaryProps> = ({
    children,
    pageName,
    onError,
}) => {
    const [resetKey, setResetKey] = React.useState(0)
    const [error, setError] = React.useState<Error | null>(null)

    const handleRetry = () => {
        setError(null)
        setResetKey((prev) => prev + 1)
    }

    const handleError = (err: Error, errorInfo: React.ErrorInfo) => {
        setError(err)
        onError?.(err, errorInfo)
    }

    return (
        <ErrorBoundary
            level="page"
            componentName={pageName}
            onError={handleError}
            resetKeys={[resetKey]}
            onReset={handleRetry}
            fallback={
                <PageErrorFallback
                    error={error}
                    onRetry={handleRetry}
                    pageName={pageName}
                />
            }
        >
            {children}
        </ErrorBoundary>
    )
}

/**
 * Excel-specific error boundary for handling Excel API errors gracefully
 */
interface ExcelErrorBoundaryProps {
    children: React.ReactNode
    onExcelError?: (error: Error) => void
    fallbackMessage?: string
}

interface ExcelErrorBoundaryState {
    hasError: boolean
    error: Error | null
    isExcelError: boolean
}

export class ExcelErrorBoundary extends React.Component<
    ExcelErrorBoundaryProps,
    ExcelErrorBoundaryState
> {
    constructor(props: ExcelErrorBoundaryProps) {
        super(props)
        this.state = {
            hasError: false,
            error: null,
            isExcelError: false,
        }
    }

    static getDerivedStateFromError(
        error: Error
    ): Partial<ExcelErrorBoundaryState> {
        const isExcelError =
            error.message?.includes('Excel') ||
            error.message?.includes('Office') ||
            error.name === 'RichApi.Error' ||
            error.message?.includes('GeneralException')

        return {
            hasError: true,
            error,
            isExcelError,
        }
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
        logError(error, errorInfo, 'excel', 'ExcelOperation')

        if (this.state.isExcelError) {
            this.props.onExcelError?.(error)
        }
    }

    reset = () => {
        this.setState({ hasError: false, error: null, isExcelError: false })
    }

    render() {
        if (this.state.hasError) {
            const message = this.state.isExcelError
                ? this.props.fallbackMessage ||
                  'Excel operation failed. Please make sure Excel is ready and try again.'
                : 'Something went wrong. Please try again.'

            return (
                <div
                    style={{
                        padding: '16px',
                        backgroundColor: this.state.isExcelError
                            ? '#fff8e6'
                            : '#fff5f5',
                        borderRadius: '8px',
                        border: `1px solid ${
                            this.state.isExcelError ? '#ffd666' : '#fed7d7'
                        }`,
                        margin: '8px 0',
                    }}
                >
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            marginBottom: '8px',
                        }}
                    >
                        <span
                            style={{
                                fontSize: '18px',
                                marginRight: '8px',
                            }}
                        >
                            {this.state.isExcelError ? '⚠️' : '❌'}
                        </span>
                        <strong
                            style={{
                                color: this.state.isExcelError
                                    ? '#ad6800'
                                    : '#c53030',
                            }}
                        >
                            {this.state.isExcelError ? 'Excel Error' : 'Error'}
                        </strong>
                    </div>
                    <p
                        style={{
                            color: '#4a5568',
                            marginBottom: '12px',
                            fontSize: '13px',
                        }}
                    >
                        {message}
                    </p>
                    <button
                        onClick={this.reset}
                        style={{
                            backgroundColor: this.state.isExcelError
                                ? '#ffc107'
                                : '#28a745',
                            color: this.state.isExcelError ? '#000' : '#fff',
                            border: 'none',
                            padding: '8px 16px',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '13px',
                        }}
                    >
                        Try Again
                    </button>
                </div>
            )
        }

        return this.props.children
    }
}

/**
 * Wrapper component that uses the hook and passes branding as prop
 * Used at the app root level
 */
export const ErrorBoundaryWithConfig: React.FC<{ children: React.ReactNode }> =
    ({ children }) => {
        // Using require to avoid circular dependency issues
        const { useClientConfig } = require('../hooks/useClientConfig')
        const { branding } = useClientConfig()

        return (
            <ErrorBoundary level="app" branding={branding}>
                {children}
            </ErrorBoundary>
        )
    }

/**
 * Hook to programmatically trigger error boundary
 * Useful for async errors that aren't caught by error boundaries
 */
export function useErrorHandler(): (error: Error) => void {
    const [, setError] = React.useState<Error | null>(null)

    return React.useCallback((error: Error) => {
        setError(() => {
            throw error
        })
    }, [])
}
